import { styled } from '@mui/material';
import { useAuth } from 'contexts/auth';
import { omit } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    AvatarButton,
    Badge,
    Box,
    Divider,
    FileUpload,
    IconButton,
    Loading,
    ResponsiveDialog,
    Stack,
    Text,
} from 'shell/components';
import { AVATAR_COLOR_OPTIONS } from 'utils/constants';
import { uploadFile } from 'utils/file';
import { EDIT_PROFILE_FIELD } from 'utils/settings';

const EditProfileImageDialog = ({ open, onClose, form, onChange }) => {
    const { auth } = useAuth();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const handleUpload = (files) => {
        const [file] = Array.from(files);
        if (!file) return;

        setIsLoading(true);
        uploadFile(
            file,
            () => {},
            (data) => {
                onChange(EDIT_PROFILE_FIELD.PROFILE_IMAGE, data);
                setIsLoading(false);
            },
            (error) => {
                console.error('File upload failed.', error);
                setIsLoading(false);
            },
            (e) => {
                console.error('File upload failed.', e);
                setIsLoading(false);
            },
            auth.owner_account_id || auth.user_id
        );
    };

    return (
        <ResponsiveDialog aria-labelledby='settings-profile-image-title' open={open} onClose={onClose}>
            <Stack spacing={4}>
                <Stack alignItems='center' direction='row' justifyContent='space-between'>
                    <Text id='settings-profile-image-title' component='h1' variant='h6' weight='semibold'>
                        {t('account-settings.profile.edit-image', 'Edit Profile Image')}
                    </Text>
                    <IconButton
                        aria-label={t('controls.close', 'Close')}
                        kind='exit'
                        size='medium'
                        variant='square'
                        onClick={onClose}
                    />
                </Stack>
                {form.profile_image ? (
                    <Box sx={{ m: 'auto' }}>
                        <Badge
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                                <DialogIcon
                                    aria-label={t('account-settings.profile.remove-image', 'Remove image')}
                                    kind='delete'
                                    size='small'
                                    variant='circle'
                                    onClick={() => onChange(EDIT_PROFILE_FIELD.PROFILE_IMAGE, null)}
                                />
                            }
                            overlap='circular'
                        >
                            <Avatar size='x-large' user={form} />
                        </Badge>
                    </Box>
                ) : isLoading ? (
                    <Loading style={{ height: 146 }} />
                ) : (
                    <FileUpload accept='image/*' onChange={handleUpload} />
                )}
                <DividerBox>
                    <Divider spacing={2} />
                    <CustomText color='light' variant='p3'>
                        {t('controls.or', 'OR')}
                    </CustomText>
                </DividerBox>
                <GridBox>
                    {AVATAR_COLOR_OPTIONS.map(({ bgColor, textColor }) => (
                        <AvatarButton
                            key={bgColor}
                            selected={
                                form.profile_circle?.bg_color === bgColor &&
                                form.profile_circle?.text_color === textColor
                            }
                            size='medium'
                            user={{
                                ...omit(form, ['profile_image']),
                                profile_circle: { bg_color: bgColor, text_color: textColor },
                            }}
                            value={{ bgColor, textColor }}
                            onClick={({ bgColor: nextBgColor, textColor: nextTextColor }) =>
                                onChange(EDIT_PROFILE_FIELD.PROFILE_CIRCLE, {
                                    bg_color: nextBgColor,
                                    text_color: nextTextColor,
                                })
                            }
                        />
                    ))}
                </GridBox>
            </Stack>
        </ResponsiveDialog>
    );
};

const DialogIcon = styled(IconButton)(({ theme }) => ({ border: `3px solid ${theme.palette.ensightful.shades[0]}` }));
const DividerBox = styled(Box)(() => ({ position: 'relative' }));

const CustomText = styled(Text)(({ theme }) => ({
    background: theme.palette.ensightful.shades[0],
    left: '50%',
    padding: theme.spacing(1),
    position: 'absolute',
    textTransform: 'uppercase',
    top: '50%',
    transform: 'translate(-50%, -50%)',
}));

const GridBox = styled(Box)(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing(4),
    gridTemplateColumns: 'repeat(6, 1fr)',
    justifyItems: 'center',
}));

export default EditProfileImageDialog;
